/**
 * Power Distribution edit form body — single "Power Distribution" details card (#996).
 * Hand-written for the 6 per-instance power distribution fields. Every field is wrapped in
 * PermissionAwareField for RBAC.
 *
 * Step-level `powerAdditionalInformation` renders here via {@link RepeatableInstanceAdditionalInfo}
 * when the standalone Power Distribution step is scoped (#997 / #1330).
 */

import React from 'react';
import { Platform, StyleSheet } from 'react-native';

import { parseOptionalNumber } from './powerDistributionListUtils';
import {
  POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_LABEL,
  POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_PLACEHOLDER,
  POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_SUBTITLE,
  POWER_DISTRIBUTION_EDIT_DETAILS_CARD_SUBTITLE,
  POWER_DISTRIBUTION_EDIT_STATUS_EXISTING_LABEL,
  POWER_DISTRIBUTION_EDIT_TYPE_LABEL,
  POWER_DISTRIBUTION_EDIT_TYPE_OPTIONS,
} from '../../constants/powerDistributionConfigurationsSubsectionCopy';
import { POWER_DISTRIBUTION_PLACEHOLDERS } from '../../constants/powerDistributionRequirementsUi';
import {
  FACILITY_POWER_NOTES_SUBSECTION_FIELDS,
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { spacing } from '../../theme';
import {
  resolveRepeatableInstanceAttachmentSectionId,
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import {
  FormRow,
  FormColumn,
  FormInput,
  UnitNumberField,
  FormTwoOptionChoice,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';
import { RepeatableInstanceAdditionalInfo } from '../repeatableSections/repeatableInstanceAdditionalInfo';

import type { SavedAttachment } from '../../services/attachmentService';
import type {
  PowerDistributionStatus,
  PowerDistributionConfiguration,
} from '../../types/powerDistribution';
import type { SelectedFile } from '../forms/FileUpload';

export interface PowerDistributionEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface PowerDistributionEditFormContentProps
  extends PowerDistributionEditFormAttachmentProps {
  formData: Partial<PowerDistributionConfiguration>;
  updateField: <K extends keyof PowerDistributionConfiguration>(
    field: K,
    value: PowerDistributionConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  testID?: string;
  /** Renders the per-instance Additional Information note when the step is scoped. */
  showAdditionalInformation?: boolean;
  showFullDetails?: boolean;
}

function powerDistributionStatusToTypeLabel(
  status: PowerDistributionStatus | undefined,
): string {
  return status === 'existing'
    ? POWER_DISTRIBUTION_EDIT_TYPE_OPTIONS[0]
    : POWER_DISTRIBUTION_EDIT_TYPE_OPTIONS[1];
}

function powerDistributionTypeLabelToStatus(
  label: string,
): PowerDistributionStatus {
  return label === POWER_DISTRIBUTION_EDIT_STATUS_EXISTING_LABEL
    ? 'existing'
    : 'new';
}

function PowerDistributionPermissionField({
  fieldId,
  requestStage,
  requestId,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  children: React.ReactNode;
}) {
  if (!requestStage) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={requestStage}
      requestId={requestId}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const POWER_DISTRIBUTION_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const PowerDistributionEditFormContent: React.FC<
  PowerDistributionEditFormContentProps
> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'power-distribution-edit-form',
  showAdditionalInformation = false,
  showFullDetails = true,
}) => {
  const permissionStage = requestStage ?? 'DRAFT';
  const editorMetricsVisibility = useFormFieldVisibilityContext({
    showFullDetails: true,
    requestStage: permissionStage,
    requestId,
  });
  const powerDistributionMetrics = subsectionHeaderMetricsProps(
    POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
    formData as Record<string, unknown>,
    editorMetricsVisibility,
  );
  const attachmentsEnabled = true;
  const configurationId = formData.id;
  const detailsBaseSectionId = SECTION_IDS.FACILITY_POWER_DISTRIBUTION;
  const detailsSectionId = resolveRepeatableInstanceAttachmentSectionId(
    detailsBaseSectionId,
    configurationId,
  );
  const detailsAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const detailsSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const additionalSectionId = resolveRepeatableInstanceAttachmentSectionId(
    SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL,
    configurationId,
  );

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={getSectionLabel(detailsBaseSectionId)}
        titleSubtitle={POWER_DISTRIBUTION_EDIT_DETAILS_CARD_SUBTITLE}
        defaultSectionExpanded
        {...POWER_DISTRIBUTION_EDITOR_SUBSECTION_PERSIST}
        {...powerDistributionMetrics}
        sectionId={detailsSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={detailsAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(detailsSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={detailsSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(detailsSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="name"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Name"
                value={formData.name || ''}
                onChangeText={value => updateField('name', value)}
                placeholder="Enter a name for this power distribution (e.g., Feed A)"
                disabled={disabled}
                testID={`${testID}-name`}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="status"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormTwoOptionChoice
                label={POWER_DISTRIBUTION_EDIT_TYPE_LABEL}
                value={powerDistributionStatusToTypeLabel(formData.status)}
                options={[
                  POWER_DISTRIBUTION_EDIT_TYPE_OPTIONS[0],
                  POWER_DISTRIBUTION_EDIT_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField(
                    'status',
                    powerDistributionTypeLabelToStatus(label),
                  )
                }
                disabled={disabled}
                inline
                testID={`${testID}-type`}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerUpsOutputVoltage"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="UPS output voltage"
                value={formData.powerUpsOutputVoltage || ''}
                onChangeText={value =>
                  updateField('powerUpsOutputVoltage', value)
                }
                placeholder={POWER_DISTRIBUTION_PLACEHOLDERS.upsOutputVoltage}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerDistributionVoltageToLoad"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="IT load voltage"
                value={formData.powerDistributionVoltageToLoad || ''}
                onChangeText={value =>
                  updateField('powerDistributionVoltageToLoad', value)
                }
                placeholder={
                  POWER_DISTRIBUTION_PLACEHOLDERS.distributionVoltageToLoad
                }
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerOverheadBuswayOrUnderFloor"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Is a busway or under floor cabling to rack desired?"
                value={formData.powerOverheadBuswayOrUnderFloor || ''}
                onChangeText={value =>
                  updateField('powerOverheadBuswayOrUnderFloor', value)
                }
                placeholder={
                  POWER_DISTRIBUTION_PLACEHOLDERS.overheadBuswayOrUnderFloor
                }
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerBuswayAmperage"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Busway amperage"
                value={formData.powerBuswayAmperage || ''}
                onChangeText={value =>
                  updateField('powerBuswayAmperage', value)
                }
                placeholder={POWER_DISTRIBUTION_PLACEHOLDERS.buswayAmperage}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerBuswayLengthFt"
              requestStage={requestStage}
              requestId={requestId}
            >
              <UnitNumberField
                fieldKey="powerBuswayLengthFt"
                unitScopeId={formData.id}
                pair="lengthFt"
                label="Busway Length"
                value={
                  formData.powerBuswayLengthFt != null
                    ? String(formData.powerBuswayLengthFt)
                    : ''
                }
                onChangeText={value =>
                  updateField('powerBuswayLengthFt', parseOptionalNumber(value))
                }
                placeholder={POWER_DISTRIBUTION_PLACEHOLDERS.buswayLengthFt}
                keyboardType="numeric"
                disabled={disabled}
                readOnlyMode={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
          <FormColumn>
            <PowerDistributionPermissionField
              fieldId="powerTapOfBoxRequirements"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Tap Off Box Requirements"
                value={formData.powerTapOfBoxRequirements || ''}
                onChangeText={value =>
                  updateField('powerTapOfBoxRequirements', value)
                }
                placeholder={
                  POWER_DISTRIBUTION_PLACEHOLDERS.tapOfBoxRequirements
                }
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </PowerDistributionPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      {showAdditionalInformation ? (
        <RepeatableInstanceAdditionalInfo
          titleSubtitle={POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_SUBTITLE}
          sectionId={additionalSectionId}
          fieldId="powerAdditionalInformation"
          inputLabel={POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_LABEL}
          placeholder={POWER_DISTRIBUTION_ADDITIONAL_INFORMATION_PLACEHOLDER}
          value={String(formData.powerAdditionalInformation ?? '')}
          onChangeText={value =>
            updateField('powerAdditionalInformation', value)
          }
          requestId={requestId}
          requestStage={permissionStage}
          disabled={disabled}
          subsectionFields={FACILITY_POWER_NOTES_SUBSECTION_FIELDS}
          headerValues={formData as Record<string, unknown>}
          attachmentsBySection={attachmentsBySection ?? {}}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles ?? {}}
          onFilesSelected={onFilesSelected}
          offlineMode={Boolean(isOffline)}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          showFullDetails={showFullDetails}
        />
      ) : null}
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
